"use client";

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import {
  THEME_COOKIE,
  THEME_STORAGE_KEY,
  type Theme,
} from "@/lib/theme-constants";

export type { Theme };
export { DEFAULT_THEME, isTheme, resolveTheme, THEME_COOKIE, THEME_STORAGE_KEY } from "@/lib/theme-constants";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  initialTheme: Theme;
  children: ReactNode;
}

export function ThemeProvider({ initialTheme, children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(initialTheme);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-theme", next);
    }
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Quota or disabled storage — fall through.
    }
    if (typeof document !== "undefined") {
      const oneYear = 60 * 60 * 24 * 365;
      document.cookie = `${THEME_COOKIE}=${next}; path=/; max-age=${oneYear}; samesite=lax`;
    }
    // Persist to DB so the preference follows the user across devices.
    // Fails silently (401, network errors) — the optimistic local update
    // above already gave the user feedback. The server cookie will resync
    // on next login.
    if (typeof fetch !== "undefined") {
      fetch("/api/user/theme", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ theme: next }),
        credentials: "same-origin",
      }).catch(() => {
        // Ignore — local state is the source of truth for this device.
      });
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
}
